<template>
  <div class="vx-card confirmation-card p-0 sm:p-8 text-center">
  <div class="success-icon-container my-12">
    <vs-icon
      icon-pack="material-icons"
      :icon="icon"
      size="70px"
      :color="color"
      class="success-icon"
    />
  </div>

    <h3 class="text-xl md:text-2xl font-semibold text-dark mb-4 leading-snug">{{ title }}</h3>
    <p class="text-base text-content mb-8 leading-relaxed">{{ msg }}</p>

    <div class="vx-card bg-theme-background p-6 mb-8">
      <div
        v-if="!statusIsCanceled"
        class="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
        <div class="text-center">
          <div class="text-sm text-content mb-1 font-medium uppercase tracking-wider">Your spot</div>
          <div class="text-2xl font-bold text-dark">{{ queuePositionLocal || '1' }}</div>
        </div>
        <div class="text-center">
          <div class="text-sm text-content mb-1 font-medium uppercase tracking-wider">Spots ahead</div>
          <div class="text-2xl font-bold text-dark">{{ spotsAheadLocal }}</div>
        </div>
      </div>

      <div class="vx-card p-4">
        <h4 class="text-sm text-content mb-3 font-semibold uppercase tracking-wider">Your details</h4>
        <div class="text-left">
            <div v-for="(detail, index) in visibleDetails" :key="detail.label">
              <div class="flex flex-col sm:flex-row sm:justify-between py-2">
                <span class="text-sm text-content font-medium mr-4 mb-1 sm:mb-0">{{ detail.label }}</span>
                <span class="text-sm text-dark font-semibold sm:text-right break-all min-w-0">{{ detail.value }}</span>
              </div>
              <vs-divider v-if="index < visibleDetails.length - 1" class="my-2"/>
            </div>
        </div>
      </div>
    </div>

    <vs-button
      v-if="statusIsCanceled"
      :style="`background: ${color} !important;
      color: ${getContrastColor(color)} !important`"
      class="w-full mb-4 shadow-md cancel-button"
      @click="confirmJoinAgain">
      Join the queue again
    </vs-button>

    <vs-button
      v-else
      :style="`background: ${color} !important;
      color: ${getContrastColor(color)} !important`"
      class="w-full mb-4 shadow-md cancel-button"
      @click="confirmCancelTurn">
      I'm not coming
    </vs-button>
  </div>
</template>

<script>
import { getContrastColor } from '@/utils/colorUtils';

import waitingListService from '@/api/modules/waiting-list.service';

export default {
  name: 'HostedWaitingListFormSuccess',
  props: {
    color: {
      type: String,
      required: true,
    },
    brandColor: {
      type: String,
      required: true,
    },
    queuePosition: {
      type: [String, Number],
      default: '1',
    },
    estimatedWait: {
      type: String,
      default: '5 mins',
    },
    customerName: {
      type: String,
      default: '',
    },
    customerPhone: {
      type: String,
      default: '',
    },
    customerEmail: {
      type: String,
      default: '',
    },
    currentTurnNumber: {
      type: [String, Number],
      default: '1',
    },
    tenantUrlLinksSuffix: {
      type: String,
      required: true,
    },
    turnInfo: {
      type: [Object, null],
      required: true,
    },
    prefixQueuePosition: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      turnInfoLocal: this.turnInfo,
      currentTurnNumberLocal: this.currentTurnNumber,
      queuePositionLocal: this.queuePosition,
      spotsAheadLocal: this.turnInfo?.spotsAhead || 0,
      pollingInterval: null,
      POLLING_INTERVAL_MS: 15000, // 15 seconds
    };
  },
  mounted() {
    this.startPolling();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  },
  beforeDestroy() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  },
  computed: {
    icon() {
      if (this.statusIsCanceled) {
        return 'close';
      }

      return 'check';
    },
    title() {
      if (this.statusIsCanceled) {
        return 'Reservation cancelled';
      }

      return 'You\'re all set!';
    },
    msg() {
      if (this.statusIsCanceled) {
        return 'Your spot has been released. You’re no longer in line and won’t receive notifications.';
      }

      return 'Your spot has been successfully reserved. We\'ll notify you when it\'s time to head back.';
    },
    visibleDetails() {
      const details = [];
      if (this.customerName) {
        details.push({ label: 'Name', value: this.customerName });
      }
      if (this.customerPhone) {
        details.push({ label: 'Phone', value: this.customerPhone });
      }
      if (this.customerEmail) {
        details.push({ label: 'Email', value: this.customerEmail });
      }
      return details;
    },
    waitingListId() {
      return this.turnInfoLocal && this.turnInfoLocal.waitingList
        ? this.turnInfoLocal.waitingList.id
        : '';
    },
    waitingListStatus() {
      return this.turnInfoLocal && this.turnInfoLocal.waitingList
        ? this.turnInfoLocal.waitingList.status
        : '';
    },
    statusIsCanceled() {
      return this.waitingListStatus === this.$enums.WaitingList.STATUS.CANCELED;
    },
    workDay() {
      return this.turnInfoLocal && this.turnInfoLocal.workDay
        ? this.turnInfoLocal.workDay
        : null;
    },
    workDayClosed() {
      return this.workDay && !this.workDay.open;
    },
    newWorkdayStarted() {
      return this.turnInfoLocal && this.turnInfoLocal.newWorkdayStarted
        ? this.turnInfoLocal.newWorkdayStarted
        : false;
    },
    turnInfoIsInvalid() {
      return (this.workDayClosed && this.statusIsCanceled) || this.newWorkdayStarted;
    },
  },
  methods: {
    getContrastColor,
    startPolling() {
      // Don't start polling if already cancelled
      if (this.statusIsCanceled) {
        return;
      }

      // Clear any existing interval
      this.stopPolling();

      // Start polling
      this.pollingInterval = setInterval(() => {
        this.fetchLatestTurnInfo();
      }, this.POLLING_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async fetchLatestTurnInfo() {
      // Don't fetch if page is hidden or status is cancelled
      if (document.hidden || this.statusIsCanceled) {
        return;
      }

      try {
        const response = await waitingListService.getTurnInfo(this.waitingListId);

        // Update current turn number
        if (response.data && response.data.currentTurnNumber !== undefined) {
          this.currentTurnNumberLocal = response.data.currentTurnNumber;
        }

        // Update spots ahead
        if (response.data && response.data.spotsAhead !== undefined) {
          this.spotsAheadLocal = response.data.spotsAhead;
        }

        // Update turn info for status checks
        if (response.data) {
          this.turnInfoLocal = response.data;
        }

        // Stop polling if turn was cancelled
        if (this.statusIsCanceled) {
          this.stopPolling();
        }
      } catch (err) {
        // Silently handle errors - don't disrupt user experience
        // Only stop polling if it's a 404 or similar critical error
        if (err.response && (err.response.status === 404 || err.response.status === 403)) {
          this.stopPolling();
        }
      }
    },
    handleVisibilityChange() {
      if (document.hidden) {
        // Page is hidden, stop polling to save resources
        this.stopPolling();
      } else {
        // Page is visible again, resume polling
        this.startPolling();
        // Fetch immediately when page becomes visible
        this.fetchLatestTurnInfo();
      }
    },
    confirmCancelTurn() {
      this.$showConfirmWarningDialog({
        title: 'Cancel your reservation?',
        text: 'Are you sure you want to cancel? You’ll lose your spot in line and stop receiving notifications.',
        acceptText: 'Yes, cancel reservation',
        cancelText: 'Keep my spot',
        accept: this.cancelTurn,
        buttonAccept: 'cancel-flat',
        buttonCancel: 'accept-fill',
      });
    },
    async cancelTurn() {
      this.$vs.loading({
        type: 'radius',
        color: this.accentColourLocal,
      });

      try {
        const response = await waitingListService.cancelTurnInWaitingList(
          this.tenantUrlLinksSuffix,
          this.waitingListId,
        );
        this.turnInfoLocal = response.data;
        this.currentTurnNumber = this.turnInfoLocal.currentTurnNumber;
        this.spotsAheadLocal = this.turnInfoLocal.spotsAhead || 0;
      } catch (err) {
        this.$router.push({ name: 'not-authorized' });
      } finally {
        this.$vs.loading.close();
      }
    },
    confirmJoinAgain() {
      this.$showConfirmActionDialog({
        title: 'Join the queue again?',
        text: 'Are you sure you want to join the queue again? You’ll get a new spot in line and start receiving notifications.',
        acceptText: 'Yes, join again',
        cancelText: 'Not joining again',
        accept: this.joinAgain,
      });
    },
    async joinAgain() {
      this.$vs.loading({
        type: 'radius',
        color: this.accentColourLocal,
      });

      try {
        const response = await waitingListService.recoverTurnInWaitingList(
          this.tenantUrlLinksSuffix,
          this.waitingListId,
        );
        this.turnInfoLocal = response.data;
        this.currentTurnNumber = this.turnInfoLocal.currentTurnNumber;
        this.spotsAheadLocal = this.turnInfoLocal.spotsAhead || 0;
        this.queuePositionLocal = this.getTurnNumberWithPrefix(
          this.turnInfoLocal.waitingList.turnNumber,
        );
      } catch (err) {
        if (err.response.data.statusCode === 400 && err.response.data.code === 'WORKDAY_CLOSED') {
          this.$emit('workday-closed');
        } else {
          this.$router.push({ name: 'not-authorized' });
        }
      } finally {
        this.$vs.loading.close();
      }
    },
    getTurnNumberWithPrefix(turnNumber) {
      const separator = this.prefixQueuePosition ? '-' : '';

      return `${this.prefixQueuePosition || ''}${separator}${turnNumber || ''}`;
    },
  },
  watch: {
    turnInfoIsInvalid: {
      immediate: true,
      handler(newValue) {
        if (newValue){
          this.$emit('workday-closed');
        }
      },
    },
  },
};
</script>

<style scoped lang="scss">
// --- SHARED STYLES ---
.confirmation-card {
  animation: slideUp 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.success-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.success-icon {
  animation: scaleIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  z-index: 2;
}

.success-icon-container::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 112px; // Fixed size that works well with 70px icon
  height: 112px;
  background: v-bind('color');
  opacity: 0.1;
  border-radius: 50%;
  z-index: 1;
}

// --- MOBILE ONLY - Hide the card styling ---
@media (max-width: 575.98px) {
  .vx-card.confirmation-card {
    background: transparent;
    border: none;
    box-shadow: none;
  }
}

// --- DESKTOP ONLY - Add extra styles ---
@media (min-width: 576px) {
  .confirmation-card {
    max-width: 500px;
    position: relative;
    overflow: hidden;

    // Top accent border decoration
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: v-bind('color');
      z-index: 1;
    }
  }
}

// --- SHARED STYLES ---

// Button hover and active states
.cancel-button {
  transition: all 0.2s ease !important;

  &:hover {
    border: 1px solid v-bind('color') !important;
    box-shadow: 0 8px 25px -8px v-bind('color') !important;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px -6px v-bind('color') !important;
  }
}

@keyframes scaleIn {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slideUp {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
